import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import moment from 'moment';

import { CONFIRM_ON_HOLD, PUT_ON_HOLD, ADD_NOTE_TO_BOOKING } from 'store/mutations';
import { bookingStatus } from 'types/booking';
import { ACTION_CODES } from 'constants/errors';
import { historyShape } from 'constants/propTypes';
import { ActionButton } from 'components/ui/Button';
import AgentPortalError from 'errors/AgentPortalError';
import { getClubEurostarSubscriptionURL } from 'utils/urls';

const PutOnHoldNoDepositButton = ({
  depositAmount,
  bookingReference,
  children,
  history,
  paymentUrlPrefix,
  isSubscription,
  membershipNumber,
  isDisabled
}) => {
  const shouldConfirmOnHold = depositAmount === 0;
  const mutation = shouldConfirmOnHold ? CONFIRM_ON_HOLD : PUT_ON_HOLD;

  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING);

  const redirect = () => {
    if (isSubscription) {
      history.push(getClubEurostarSubscriptionURL(membershipNumber, bookingReference), {
        shouldRetry: true
      });
    } else {
      history.push(`/manage/bookings/${bookingReference}/journeys`);
    }
  };

  const handleCompleted = async (data) => {
    const {
      putOnHold: { status: tempOnHoldStatus },
      confirmOnHold: { status: onHoldStatus, onHold: { expiryTimestamp } = {} } = {}
    } = data;

    if (shouldConfirmOnHold && tempOnHoldStatus === bookingStatus.TEMPORARILY_ON_HOLD && onHoldStatus === bookingStatus.ON_HOLD) {
      const DATE_FORMAT = 'ddd Do MMMM YYYY [at] HH:mm';
      const formattedExpiryTimestamp = moment(expiryTimestamp).utc().format(DATE_FORMAT);

      await addNote({
        variables: {
          bookingReference: bookingReference,
          note: `On-hold booking expiry time: ${formattedExpiryTimestamp} UTC`
        }
      });
    }

    redirect();
  };

  const [putOnHold, { error, loading }] = useMutation(mutation, {
    variables: { bookingReference },
    onCompleted: handleCompleted
  });

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}`
    });
  }

  return (
    <ActionButton onClick={putOnHold} data-cy="on-hold-btn" loading={loading} disabled={isDisabled}>
      {children}
    </ActionButton>
  );
};

PutOnHoldNoDepositButton.propTypes = {
  depositAmount: PropTypes.number.isRequired,
  history: historyShape,
  children: PropTypes.node,
  paymentUrlPrefix: PropTypes.string,
  bookingReference: PropTypes.string,
  isSubscription: PropTypes.bool,
  membershipNumber: PropTypes.string,
  isDisabled: PropTypes.bool
};

export default PutOnHoldNoDepositButton;
